import RAPIER from '@dimforge/rapier3d-compat'

import { physicalPropertiesFor } from './materialPhysicalProperties'
import { startKeyboardTargetLifecycle } from '../animationWindow'
import { isTextEntryTarget } from '../eventGuards'
import { safeHalf } from './colliderShapes'
import {
  ARROW_KEYS,
  DASH_KEYS,
  directionFromKeys,
  MOVEMENT_KEYS,
} from './inputKeys'
import { createObjectBodyRegistry } from './objectBodyRegistry'
import { createPlatformCarry } from './platformCarry'
import { programPushDistance } from './programPushGeometry'
import {
  AUTOSTEP_MAX_HEIGHT,
  AUTOSTEP_MIN_WIDTH,
  CONTROLLER_OFFSET,
  FIXED_STEP,
  GRAVITY,
  GROUND_CONTACT_MARGIN,
  GROUND_RAY_DIRECTION,
  GROUND_TOUCH_MARGIN,
  JUMP_POWER_SCALE,
  MAX_FRAME_DT,
  MAX_SLOPE_CLIMB_ANGLE,
  MIN_SLOPE_SLIDE_ANGLE,
  RESPAWN_DELAY_SECONDS,
  SLIDE_ACCELERATION,
  SLIDE_EXIT_SPEED,
  SLIDE_FRICTION,
  SLIDE_MAX_SPEED,
  SLIDE_OBJECT_NAME,
  SWIM_BUOYANCY_SPEED,
  SWIM_HORIZONTAL_SCALE,
  SWIM_JUMP_SPEED,
  SWIM_MIN_DEPTH,
  SWIM_SURFACE_CENTER_DEPTH,
  SWIM_UP_SPEED,
  SWIM_VERTICAL_DRAG,
  WALK_SPEED_SCALE,
} from './physicsConstants'
import {
  composeYaw,
  interpolateStepPositions,
  normalizeAngleDelta,
  normalizeXZ,
  rotateXZByYaw,
  yawFromQuat,
} from './poseMath'
import {
  computeFallThreshold,
  createStaticSceneColliders,
} from './staticSceneColliders'
import { createTouchDetection } from './touchDetection'
import type {
  AvatarMotionState,
  ObjectRuntimeState,
  PreviewWorld,
  SceneDescriptor,
  Vec3,
} from '../types'
import { AVATAR_ID } from '../types'
import { createVehicleSystem } from './vehicleSystem'
import type { VehiclePromptKind } from './vehicleSystem'

// テスト等が physicsController から直接 import している既存パスを維持する再エクスポート。
export { WALK_SPEED_SCALE } from './physicsConstants'
export { interpolateStepPositions } from './poseMath'
export type { VehiclePromptKind } from './vehicleSystem'

/**
 * キー操作アバターの物理制御ハンドル。
 *
 * rapier の物理 World（衝突解決の真実源）と PreviewWorld（見た目・状態の真実源）の
 * 二重管理を毎フレーム同期する。具体的には:
 * - シーンオブジェクト: PreviewWorld → rapier（state.position の変化を Fixed body へ反映）
 * - アバター: rapier → PreviewWorld（衝突解決後の位置を world.setPosition へ反映）
 * という一方向ずつの同期で、循環参照を避ける。
 */
export interface AvatarController {
  /**
   * アバターをスポーン位置・初期向きへ戻し、速度・接触履歴をリセットする。
   * スタート地点オブジェクトの姿勢も初期へ戻す（停止ボタンのように
   * world.reset() を伴わない経路でも、向きが最初の状態へ揃うように）。
   */
  reset(): void
  /**
   * アバターの向きと台追従のヨー基準を、スタート地点の現在の向きへ即時整合
   * させる（実行開始時に runtime.start() の直後に呼ぶ）。「スタートしたとき」
   * の最初の文がスタート地点を回した場合に、carry 追従（次の物理ステップ）と
   * 監視窓のカメラ再スナップを待たず、描画前に向きを確定させる（回転前の
   * 向きが数フレーム描画されて「一瞬前の角度」に見えるのを防ぐ）。
   */
  syncSpawnFacing(): void
  /**
   * 物理シミュレーションとキー入力を一時停止/再開する（停止ボタン用）。
   * 一時停止中はアバター・オブジェクトの物理が進まず、移動キーも受け付けない。
   */
  setPaused(paused: boolean): void
  /** ループ停止・リスナ解除・物理リソース解放。冪等。 */
  dispose(): void
  /**
   * 現在の瞬時モーション状態を返す。レンダラ側が毎フレーム参照して
   * 屈伸・空中・着地のポーズアニメを合成する。
   * 物理 WASM の参照は内部で完結し、外部からは値型のスナップショットを返す。
   *
   * @param nowMs renderPosition（描画補間位置）の評価時刻。レンダラの rAF
   *   タイムスタンプを渡すこと。物理ループも rAF タイムスタンプで時間を進める
   *   ため、同じ vsync 基準の時計で評価すると 2 つのループの実行順・位相ずれの
   *   影響を受けない。省略時は performance.now()（テスト用フォールバック）。
   */
  getMotionState(nowMs?: number): AvatarMotionState
  /**
   * 画面上の操作ボタン（キーガイドのタップ等）からの仮想キー入力。
   * キーボードの keydown/keyup と同じ意味論で処理する（タブレット対応）。
   * code は KeyboardEvent.code 相当（'KeyW' / 'Space' など）。
   */
  setVirtualKey(code: string, pressed: boolean): void
  /**
   * 乗降アクション（E キーと同じ意味論）。画面上の乗降ボタンから呼ぶ。
   * 車両の近く（プロンプト表示中）なら乗車、乗車中なら降車する。
   * 一時停止・死亡演出中は無視する。
   */
  triggerVehicleAction(): void
}

// ダッシュ時の移動速度倍率（ダッシュ機能の無効化に伴いコメントアウト。
// 歩行モーションを走りへ統一したため、速度倍率は使わない。再有効化時に戻す）。
// Shift を押しながら移動キーを入力している間、現在の歩行速度
// （events_setWalkSpeedTo で変わり得る）にこの倍率を掛ける仕様だった。
// const DASH_SPEED_MULTIPLIER = 1.8

/**
 * シーン記述から物理アバターコントローラを生成する。
 *
 * RAPIER.init() が非同期なため Promise を返す。descriptor.avatar が無い場合は
 * 物理を構築せず no-op なハンドルを返す（呼び出し側の分岐を不要にするため）。
 *
 * @param descriptor シーン記述。objects と avatar と vehicles を読む。
 * @param world 見た目・状態の真実源。位置/向きの反映先かつオブジェクト位置の参照元。
 * @param onTouch アバターが新たにオブジェクトへ接触した瞬間に objectId で呼ばれる。
 * @param keyboardTarget キー入力を受け取る要素。プレビュー領域にスコープを限定する。
 */
export const createAvatarController = async (
  descriptor: SceneDescriptor,
  world: PreviewWorld,
  onTouch: (objectId: string) => void,
  keyboardTarget: HTMLElement,
  // follow（アバター視点）時の現在カメラ方位（ラジアン）。返り値が非 null のとき
  // 移動入力をこの方位で回転し、視点を向けた方向へ進める（カメラ相対移動）。
  // null（free/first/未指定）ではワールド基準の従来移動。
  getCameraYaw?: () => number | null,
  // 乗降プロンプトの表示状態が変わった瞬間に呼ばれる（UI の乗降ボタン表示用）。
  onVehiclePrompt?: (prompt: VehiclePromptKind) => void,
): Promise<AvatarController> => {
  const avatar = descriptor.avatar
  if (!avatar) {
    // アバター不在のシーンでは物理は不要。安全な no-op を返す。
    return {
      reset: () => {},
      syncSpawnFacing: () => {},
      setPaused: () => {},
      dispose: () => {},
      getMotionState: () => ({
        grounded: true,
        verticalVelocity: 0,
        isDashing: false,
        isSwimming: false,
        isSwimmingMoving: false,
        isSliding: false,
        isSeated: false,
        respawnGeneration: 0,
        deathKind: null,
        deathFall: null,
        renderPosition: null,
      }),
      setVirtualKey: () => {},
      triggerVehicleAction: () => {},
    }
  }

  await RAPIER.init()

  const physicsWorld = new RAPIER.World({ x: 0, y: -GRAVITY, z: 0 })
  const characterController =
    physicsWorld.createCharacterController(CONTROLLER_OFFSET)
  // 薄い床や段差での貫通・引っかかりを抑える。
  characterController.enableSnapToGround(0.5)
  // 低い段差（厚み 1 の床の縁など）はジャンプ不要で歩いて登れるようにする。
  characterController.enableAutostep(
    AUTOSTEP_MAX_HEIGHT,
    AUTOSTEP_MIN_WIDTH,
    false,
  )
  // 登坂・滑落の斜度を Roblox の Humanoid 既定（89°）へ合わせる。
  characterController.setMaxSlopeClimbAngle(MAX_SLOPE_CLIMB_ANGLE)
  characterController.setMinSlopeSlideAngle(MIN_SLOPE_SLIDE_ANGLE)
  // アンカー解除した動的剛体（大玉など）をアバターが歩いて押せるようにする。
  characterController.setApplyImpulsesToDynamicBodies(true)

  // オブジェクト body の生成・World↔rapier 同期・リセットはレジストリへ委譲する。
  // collider handle → objectId / 適用モードの参照（touch 検知・carry・vehicle 等）
  // もレジストリの lookup API（objectIdOf / modeOf / has）を経由する。
  let disposed = false
  const objectBodyRegistry = createObjectBodyRegistry(
    physicsWorld,
    world,
    descriptor,
    () => disposed,
    // 押し出し蓄積はアバター body の寸法に依存するため後方で定義される。
    // 構築時ではなく sync 時（最初の物理ステップ以降）に評価されるよう
    // ラップして渡す。
    (id, state, from, to, colliderHandle) =>
      accumulateProgramPush(id, state, from, to, colliderHandle),
    (id, state, previousSize, nextSize, colliderHandle) =>
      accumulateProgramResizePush(
        id,
        state,
        previousSize,
        nextSize,
        colliderHandle,
      ),
  )

  createStaticSceneColliders(physicsWorld, descriptor)

  // 水域（terrain.water）。アバターの足元がこの中で深く水没したとき簡易遊泳になる。
  const waterVolume = descriptor.terrain?.water ?? null

  // スポーン向きの追従元になる「スタート地点」オブジェクト。実機はキャラが
  // SpawnLocation の現在の向きでスポーンするため、学習者プログラムが
  // スタート地点を回したら、以後のスポーン/リスポーンはその向きを使う。
  // converter は avatar.spawn を「SpawnLocation の中心 XZ・上端 + アバター半高」
  // で導出するので、同じ式を満たすシーンオブジェクトを SpawnLocation とみなす
  // （該当なしなら null＝従来どおり固定の spawnYaw を使う）。
  const SPAWN_MATCH_EPSILON = 0.01
  const spawnObjectId =
    descriptor.objects.find(
      (object) =>
        Math.abs(object.position.x - avatar.spawn.x) <= SPAWN_MATCH_EPSILON &&
        Math.abs(object.position.z - avatar.spawn.z) <= SPAWN_MATCH_EPSILON &&
        Math.abs(
          object.position.y +
            object.size.y / 2 +
            avatar.size.y / 2 -
            avatar.spawn.y,
        ) <= SPAWN_MATCH_EPSILON,
    )?.id ?? null

  /** オブジェクト状態の合計ヨー（基準姿勢 + 実行時ヨー）。 */
  const totalYawOf = (state: {
    orientation: { x: number; y: number; z: number; w: number }
    rotationY: number
  }): number => yawFromQuat(composeYaw(state.orientation, state.rotationY))

  // スタート地点の初期ヨー。avatar.spawnYaw はこの向きから導出されている。
  // 現在ヨーとの差分だけを spawnYaw へ加算することで、絶対指定（〜どにする）と
  // 相対指定（〜どまわす）のどちらの回し方でも実機と同じ向きになる。
  // ライブな World 状態ではなく descriptor（初期姿勢）から決定的に導出する。
  // autoRun のページ（scene-viewer 等）では学習者プログラムの同期セグメントが
  // コントローラ構築（RAPIER 初期化の await 後）より先に走り得るため、構築時の
  // World を「初期状態」とみなすと回転後のヨーを基準に取り込んでしまう。
  const initialSpawnObjectYaw = (() => {
    if (!spawnObjectId) {
      return 0
    }
    const object = descriptor.objects.find((o) => o.id === spawnObjectId)
    if (!object) {
      return 0
    }
    return totalYawOf({
      orientation: object.orientation ?? { x: 0, y: 0, z: 0, w: 1 },
      rotationY: object.rotationY ?? 0,
    })
  })()

  /** 現時点のスポーン向き（スタート地点が回された分を spawnYaw へ反映）。 */
  const currentSpawnYaw = (): number => {
    const base = avatar.spawnYaw ?? 0
    const state = spawnObjectId ? world.getState().get(spawnObjectId) : null
    if (!state) {
      return base
    }
    return base + normalizeAngleDelta(totalYawOf(state) - initialSpawnObjectYaw)
  }

  // アバター body。初期位置は World 上のアバター状態を優先し、無ければ spawn。
  const avatarState = world.getState().get(AVATAR_ID)
  const initialPosition = avatarState ? avatarState.position : avatar.spawn
  const avatarBodyDesc =
    RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
      initialPosition.x,
      initialPosition.y,
      initialPosition.z,
    )
  const avatarBody = physicsWorld.createRigidBody(avatarBodyDesc)
  // アバターはカプセルコライダー（Roblox の Humanoid HipHeight/丸い足元に近い）。
  // 箱だと縁で角が引っかかり、autostep/snapToGround と相まって段差で詰まりやすい。
  // 半径 = 水平方向の最小半幅、半高（円筒部）= 全高/2 - 半径。退化を避けて下限クランプ。
  // rapier の capsule は Y 軸（縦）が長軸で、全高 = 2*(halfHeight + radius) となるため、
  // 箱と同じ全高（size.y）・接地中心を保ち spawn の足裏位置を変えない。
  // 半径は全高/2 以下に抑える（横幅が縦より大きい潰れた寸法でも全高が size.y を超えない）。
  const avatarRadius = Math.min(
    safeHalf(Math.min(avatar.size.x, avatar.size.z)),
    avatar.size.y / 2,
  )
  const avatarHalfHeight = Math.max(
    Number.EPSILON,
    avatar.size.y / 2 - avatarRadius,
  )
  const avatarColliderDesc = RAPIER.ColliderDesc.capsule(
    avatarHalfHeight,
    avatarRadius,
  )
  const avatarProperties = physicalPropertiesFor('Plastic')
  avatarColliderDesc.setFriction(avatarProperties.friction)
  avatarColliderDesc.setRestitution(avatarProperties.elasticity)
  avatarColliderDesc.setFrictionCombineRule(
    RAPIER.CoefficientCombineRule.Average,
  )
  avatarColliderDesc.setRestitutionCombineRule(
    RAPIER.CoefficientCombineRule.Average,
  )
  const avatarCollider = physicsWorld.createCollider(
    avatarColliderDesc,
    avatarBody,
  )

  /**
   * クエリパイプライン（レイ/シェイプキャストが参照する構造）を現在の
   * コライダー姿勢で再構築する。rapier のクエリは world.step() で構築される
   * 構造を参照するため、(a) 一度も step していない生成直後や、(b) body を
   * テレポートで動かした直後（objectBodyRegistry.reset）は、古い/空の構造のままに
   * なりレイが外れる。timestep=0 の step はシミュレーション（重力・速度・
   * イベント）を進めずにクエリ構造だけを更新できる。
   */
  const warmUpQueries = (): void => {
    const previousTimestep = physicsWorld.timestep
    physicsWorld.timestep = 0
    try {
      physicsWorld.step()
    } finally {
      physicsWorld.timestep = previousTimestep
    }
  }

  // 生成直後の初期化。初回プレイは一時停止のまま reset() が走り、その中の
  // rememberGroundPlatform の接地レイが未初期化のクエリでは何にもヒットしない。
  warmUpQueries()

  const fallThreshold = computeFallThreshold(descriptor, avatar.spawn.y)

  const held = new Set<string>()
  // 仮想キー（キーガイドのタップ等）の押下集合。キーボードの held と分離して、
  // 同じキーを物理・仮想の両方で押したとき片方の解放がもう片方を消さないようにする。
  const virtualHeld = new Set<string>()
  // ジャンプキー（Space）の押しっぱなし状態。物理（キーボード）と仮想の集合で
  // 持ち、遊泳中の「押している間だけ浮上する」操作に使う（地上ジャンプは
  // 押下イベント時の applyKeyPress で即時処理するため held には載らない）。
  const jumpHeldSources = new Set<'physical' | 'virtual'>()
  let velocityY = 0
  // 直近のステップで接地していたか。Space 押下時のジャンプ可否判定に使う。
  let grounded = true
  /**
   * 死亡演出の状態。null は生存。死亡中はアバターを動かさず操作も受け付けず、
   * remaining が尽きたら spawn へ復帰する（Roblox の RespawnTime 既定 3 秒）。
   * - health: 体力 0。レンダラは倒れるポーズを表示する。fall は倒れ込む向きと
   *   最大倒れ角（壁などを貫通しないよう、空いている側・届く角度を物理レイで決める）。
   * - void: 奈落落下。レンダラはアバターを非表示にし、カメラは消えた場所に留まる。
   */
  let death: {
    kind: 'health' | 'void'
    remaining: number
    fall: { direction: 'forward' | 'backward'; maxAngleRad: number } | null
  } | null = null
  // Shift（ダッシュ修飾キー）の押下状態。左右どちらか 1 つでも押されていればダッシュ。
  // boolean だと両押し後に片方を離した時に誤って解除されるため Set で個別追跡する。
  const heldDashKeys = new Set<string>()
  // 直近ステップのダッシュ判定。レンダラへ motion state として渡す。
  let isDashing = false
  // 直近ステップの遊泳判定（移動後位置基準）。レンダラが泳ぐポーズに使う。
  let isSwimmingNow = false
  // 遊泳中の移動キー入力有無。平泳ぎ（入力あり）と浮遊（入力なし）の切替に使う。
  let isSwimmingMovingNow = false
  // すべり台滑走中か。乗ると操作不能で滑り降り、台から出て減速したら解除。
  let isSlidingNow = false
  // 滑走速度（studs/s）と水平の滑走方向（正規化済み）。
  let slideSpeed = 0
  let slideDirection = { x: 0, z: 0 }
  // 接地斜面の勾配（水平 1 進むときの下がり量）。滑走中の貼り付き下降に使う。
  let slideGradient = 0
  // spawn へテレポート（リスポーン/リセット）した回数。レンダラはこの単調増加値の
  // 変化で「テレポート復帰フレーム」を検出し、誤った着地屈伸を抑止する。
  let respawnGeneration = 0
  // reset 直後の数ステップで「スポーン向きが変わっていたらカメラを背後へ
  // 再スナップさせる」ための残りステップ数と基準ヨー。再生開始時の学習者
  // プログラム（onPlay の同期実行）がスタート地点を回すと、アバターは台の
  // 追従（platformCarry）で回るが、カメラのスナップは reset 時の向きで消費
  // 済みのため、向きだけ取り残される（キャラとカメラが別方向を向く）。
  // 追従の適用は接地記録の settle を挟んで 1〜2 ステップ遅れることがあるため、
  // 1 ステップではなく短い猶予窓で監視する。
  const SPAWN_FACING_SNAP_WINDOW_STEPS = 3
  let spawnFacingSnapSteps = 0
  let spawnFacingYawAtReset = 0
  // spawn/テレポート直後の接地 settle 猶予ステップ。KCC は spawn 台に乗っていても
  // 最初の 1〜2 step は computedGrounded()=false を返すことがあり、それを真に受けると
  // 偽の落下・着地屈伸（足が台へ一瞬めり込む）になる。猶予中は接地を維持して防ぐ。
  let spawnSettleSteps = 2
  let accumulator = 0
  // 描画補間用: 直近 3 物理ステップのアバター確定位置（古→新）。物理は 1/60s
  // 固定ステップ、描画は別の rAF ループ（60〜120Hz）で進むため、ステップ確定
  // 位置をそのまま描くと「進むフレーム・止まるフレーム」が混在して歩行が
  // ガタつく（時間エイリアシング）。さらに 2 つの rAF は実行順・発火フレームが
  // 半〜1 フレームずれるため、直近 2 ステップだけの補間では描画側が物理より
  // 先行した瞬間に係数が上限へ張り付き、「1 ステップ跳ぶ→止まる」が再発する。
  // そこで 3 サンプルを保持し、表示時刻を最新ステップから 2 ステップ
  // （≒33ms）遅らせて区分線形補間する。これでどちらのループが先行しても
  // 表示時刻は常にサンプル区間内に収まり、なめらかに動く。
  // motionState.renderPosition は表示専用で、World の位置（判定・スクリプトの
  // 真実源）は物理確定位置のまま変えない。テレポート時はスナップ。
  let renderPosSamples: [
    { x: number; y: number; z: number },
    { x: number; y: number; z: number },
    { x: number; y: number; z: number },
  ] = [{ ...avatar.spawn }, { ...avatar.spawn }, { ...avatar.spawn }]
  // 最新ステップを消費し終えた仮想時刻（ms、rAF タイムスタンプ基準
  // = now - accumulator）。renderPosition はレンダラの rAF タイムスタンプとの
  // 差（vsync 基準の同一時計）で表示時刻を決める。
  let lastStepSettledAtMs = performance.now()
  let lastTime = performance.now()
  // 一時停止フラグ（停止ボタン）。true の間は物理ステップとキー入力を止める。
  // rAF ループ自体は回し続け、再開時に lastTime 差分の巨大 dt が出ないようにする。
  let paused = false

  /**
   * プログラム移動する solid 部品がアバターへ食い込んだときの押し出し要求。
   * Roblox の Anchored パーツが移動で Humanoid を押す挙動の近似（RJF10 の
   * 赤/黄ブロック等）。
   *
   * sync（部品を動かした瞬間）では「どの部品を・どちらへ逃がすか」だけを
   * 要求として積み、必要な押し出し量は適用時点（台追従 applyCarry の後）の
   * アバター位置から解き直す。回転した部品の境界は斜めなので、必要な退避量は
   * アバターの横位置によって変わる（45° の板では 1.7 studs 以上変動する）。
   * sync 時点の位置で平面を確定してしまうと、その後 carry でアバターが横へ
   * 運ばれたときに押し出しが足りず、部品へめり込んだまま残ることがある。
   *
   * 解いた結果は「アバターが満たすべき分離平面」に畳む。同方向の複数部品では
   * 最も厳しい平面だけが残るため過剰押しにならない。
   * 押し出しは同一ステップの KCC 移動へ合流させ、進路上の壁など他コライダー
   * との衝突は KCC に任せる。押している部品自身は KCC の障害物から除外する
   * （食い込み状態で押し出しがブロックされないようにする）。
   */
  interface ProgramPushPlanes {
    xMin?: number
    xMax?: number
    zMin?: number
    zMax?: number
  }
  interface ProgramPushVector {
    x: number
    z: number
  }
  /** sync が積む押し出し要求（部品の移動後の姿勢と、逃がす向き）。 */
  interface ProgramPushRequest {
    id: string
    colliderHandle: number | undefined
    box: {
      center: { x: number; y: number; z: number }
      rotation: { x: number; y: number; z: number; w: number }
      half: { x: number; y: number; z: number }
    }
    direction: { x: number; y: number; z: number }
  }
  let programPushRequests: ProgramPushRequest[] | null = null
  let programPushVector: ProgramPushVector | null = null
  const pushingColliderHandles = new Set<number>()
  // この蓄積分で押している部品の id。KCC から除外すると computedCollision に
  // 載らず Touched が発火しなくなるため、detectTouches で接触として補う。
  let pushingObjectIds = new Set<string>()
  // detectTouches が参照する「このステップで押していた部品」。
  let pushedThisStep: ReadonlySet<string> = new Set<string>()

  /**
   * 押し出し後にブロック前面から空ける余白（studs）。前面ぴったりだと
   * 次フレームの移動で再び食い込み、押し出しが毎フレーム振動するため。
   */
  const PROGRAM_PUSH_MARGIN = 0.05

  /** サイズ変更の押し出しで試す水平 4 方向（最小の必要量を選ぶ）。 */
  const HORIZONTAL_ESCAPES: ReadonlyArray<{ x: number; y: number; z: number }> =
    [
      { x: 1, y: 0, z: 0 },
      { x: -1, y: 0, z: 0 },
      { x: 0, y: 0, z: 1 },
      { x: 0, y: 0, z: -1 },
    ]

  const addProgramPushVector = (
    id: string,
    push: ProgramPushVector,
    colliderHandle: number | undefined,
  ): void => {
    if (Math.abs(push.x) < 1e-6 && Math.abs(push.z) < 1e-6) {
      return
    }
    programPushVector =
      programPushVector ?
        { x: programPushVector.x + push.x, z: programPushVector.z + push.z }
      : push
    pushingObjectIds.add(id)
    if (colliderHandle !== undefined) {
      pushingColliderHandles.add(colliderHandle)
    }
  }

  /**
   * 部品のプログラム移動（from→to）を押し出し要求として積む。
   * ここでは「移動後の姿勢」と「進行方向の支配軸へ逃がす」ことだけを記録し、
   * 重なり判定と必要な押し出し量は resolveProgramPushRequests が適用時点の
   * アバター位置で解く（回転した部品の斜めの境界では、必要な退避量がアバターの
   * 横位置で変わるため。詳細は ProgramPushRequest の説明を参照）。
   * 縦（Y）方向だけの移動は対象外（押し潰しの再現はしない）。
   * objectBodyRegistry の sync（Fixed body の離散テレポート適用時）から
   * コールバックで呼ばれる。collider は registry 側の所有物のため、
   * KCC 除外に使う handle は引数で受け取る。
   */
  const accumulateProgramPush = (
    id: string,
    state: ObjectRuntimeState,
    from: { x: number; y: number; z: number },
    to: { x: number; y: number; z: number },
    colliderHandle: number | undefined,
  ): void => {
    if (!state.canCollide) {
      return
    }
    const deltaX = to.x - from.x
    const deltaZ = to.z - from.z
    if (deltaX === 0 && deltaZ === 0) {
      return
    }
    // 押し出す向き（移動量の支配軸）。縦方向だけの移動はここへ来ない。
    const pushX = Math.abs(deltaX) >= Math.abs(deltaZ)
    const sign = (pushX ? deltaX : deltaZ) > 0 ? 1 : -1
    ;(programPushRequests ??= []).push({
      id,
      colliderHandle,
      box: {
        center: { x: to.x, y: to.y, z: to.z },
        rotation: composeYaw(state.orientation, state.rotationY),
        half: {
          x: state.size.x / 2,
          y: state.size.y / 2,
          z: state.size.z / 2,
        },
      },
      direction: pushX ? { x: sign, y: 0, z: 0 } : { x: 0, y: 0, z: sign },
    })
  }

  /**
   * 積まれた押し出し要求を、現在のアバター位置（台追従 applyCarry の後）で
   * 解いて分離平面へ畳む。実際に重なっている要求だけが平面に反映され、
   * その部品が「押している部品」として KCC 除外・Touched 補完の対象になる。
   *
   * 部品の実際の姿勢（回転した直方体）で解くため、ワールド AABB 近似のときの
   * ように実体の無い角で押したり（見えない当たり判定）、AABB の面まで数 studs
   * 飛ばしたり（ワープ）しない。軸平行な部品では従来式（面 + アバター半径 +
   * 余白）と同じ値になる。
   */
  const resolveProgramPushRequests = (
    requests: ReadonlyArray<ProgramPushRequest>,
    current: { x: number; y: number; z: number },
    pushedIds: Set<string>,
    excludeHandles: Set<number>,
  ): ProgramPushPlanes | null => {
    let planes: ProgramPushPlanes | null = null
    const avatarBox = {
      center: current,
      half: {
        x: avatarRadius,
        y: avatarHalfHeight + avatarRadius,
        z: avatarRadius,
      },
    }
    for (const request of requests) {
      const distance = programPushDistance({
        box: request.box,
        avatar: avatarBox,
        direction: request.direction,
        margin: PROGRAM_PUSH_MARGIN,
      })
      if (distance === null || distance === 0) {
        continue
      }
      const next = (planes ??= {})
      if (request.direction.x > 0) {
        next.xMin = Math.max(next.xMin ?? -Infinity, current.x + distance)
      } else if (request.direction.x < 0) {
        next.xMax = Math.min(next.xMax ?? Infinity, current.x - distance)
      } else if (request.direction.z > 0) {
        next.zMin = Math.max(next.zMin ?? -Infinity, current.z + distance)
      } else {
        next.zMax = Math.min(next.zMax ?? Infinity, current.z - distance)
      }
      pushedIds.add(request.id)
      if (request.colliderHandle !== undefined) {
        excludeHandles.add(request.colliderHandle)
      }
    }
    return planes
  }

  /**
   * looks_setSizeTo 等で solid 部品が拡大し、アバターを巻き込んだときの押し出し。
   * 位置移動ではないため進行方向が無い。球は中心から外へ、箱系は水平 4 方向の
   * うち押し出し量が最小の向きへ押す（実機の Humanoid が横へ逃げる挙動の近似）。
   * 箱系の重なり・押し出し量は移動の押し出しと同じく実際の姿勢で解く
   * （回した部品を AABB で近似すると、触れていない角でも押されてしまう）。
   */
  const accumulateProgramResizePush = (
    id: string,
    state: ObjectRuntimeState,
    previousSize: { x: number; y: number; z: number },
    nextSize: { x: number; y: number; z: number },
    colliderHandle: number | undefined,
  ): void => {
    if (!state.canCollide) {
      return
    }
    const avatar = avatarBody.translation()
    const dx = avatar.x - state.position.x
    const dz = avatar.z - state.position.z

    if (state.shape === 'ball') {
      const radius = safeHalf(Math.min(nextSize.x, nextSize.y, nextSize.z))
      const halfSumY = radius + avatarHalfHeight + avatarRadius
      if (Math.abs(avatar.y - state.position.y) >= halfSumY) {
        return
      }
      const distance = Math.hypot(dx, dz)
      const requiredDistance = radius + avatarRadius + PROGRAM_PUSH_MARGIN
      if (distance >= requiredDistance) {
        return
      }
      const growthX = Math.max(0, nextSize.x - previousSize.x)
      const growthZ = Math.max(0, nextSize.z - previousSize.z)
      const direction =
        distance > 1e-6 ? { x: dx / distance, z: dz / distance }
        : growthZ > growthX ? { x: 0, z: 1 }
        : { x: 1, z: 0 }
      const amount = requiredDistance - distance
      addProgramPushVector(
        id,
        { x: direction.x * amount, z: direction.z * amount },
        colliderHandle,
      )
      return
    }

    // 水平 4 方向（±X / ±Z）すべての必要量を実姿勢で求め、いちばん少ない
    // 向きへ逃がす。回した細長い部品では、最寄りの面が「中心から見て
    // アバターがいる側」とは限らない（斜めの断面では中心側へ動くほうが先に
    // 面へ抜ける）。±X / ±Z のうち 2 方向しか見ないと、部品を大きく横切る
    // 押し出しになって見た目に飛ぶ。
    const query = {
      box: {
        center: state.position,
        rotation: composeYaw(state.orientation, state.rotationY),
        half: { x: nextSize.x / 2, y: nextSize.y / 2, z: nextSize.z / 2 },
      },
      avatar: {
        center: avatar,
        half: {
          x: avatarRadius,
          y: avatarHalfHeight + avatarRadius,
          z: avatarRadius,
        },
      },
      margin: PROGRAM_PUSH_MARGIN,
    }
    let best: { x: number; z: number; distance: number } | null = null
    for (const direction of HORIZONTAL_ESCAPES) {
      const distance = programPushDistance({ ...query, direction })
      if (distance === null) {
        // 実体には巻き込まれていない（この向きで分離済み＝重なっていない）。
        return
      }
      if (!best || distance < best.distance) {
        best = { x: direction.x, z: direction.z, distance }
      }
    }
    if (!best || best.distance === 0) {
      return
    }
    addProgramPushVector(
      id,
      { x: best.x * best.distance, z: best.z * best.distance },
      colliderHandle,
    )
  }

  /**
   * 分離平面を現在のアバター位置で解決し、必要最小の押し出し量を返す。
   * 逆方向の平面が両立する（部品に挟まれた）場合は xMax/zMax 側を優先して
   * 一方だけ満たす（実機の圧死・吹き飛びまでは再現しない）。
   */
  const resolveProgramPush = (
    planes: ProgramPushPlanes,
    current: { x: number; z: number },
  ): { x: number; z: number } => {
    let x = 0
    if (planes.xMin !== undefined && current.x < planes.xMin) {
      x = planes.xMin - current.x
    }
    if (planes.xMax !== undefined && current.x > planes.xMax) {
      x = planes.xMax - current.x
    }
    let z = 0
    if (planes.zMin !== undefined && current.z < planes.zMin) {
      z = planes.zMin - current.z
    }
    if (planes.zMax !== undefined && current.z > planes.zMax) {
      z = planes.zMax - current.z
    }
    return { x, z }
  }

  /**
   * 押し出し軸の移動量へ歩行入力を合流させる。押し返す方向への入力で
   * 押し出しが相殺される（再び食い込む）のを防ぐため、逆向き入力は無視し、
   * 同方向の入力だけ加算する。
   */
  const mergePushAxis = (input: number, push: number): number => {
    if (push === 0) {
      return input
    }
    return Math.sign(input) === Math.sign(push) ? push + input : push
  }

  /**
   * 指定方向の水平クリアランス（最寄り障害物までの距離、studs）を測る。
   * 倒れ込みポーズが壁などを貫通しないよう、空いている側の判定に使う。
   * 倒れた身体は地面近くを占有するため、足元・腰・胸の複数高さから
   * レイを撃ち、低い障害物の見逃しを防ぐ。sensor（CanCollide=false）は
   * 障害物でないため除外する。
   */
  const horizontalClearance = (direction: {
    x: number
    y: number
    z: number
  }): number => {
    const bodyLength = (avatarHalfHeight + avatarRadius) * 2
    const center = avatarBody.translation()
    const footY = center.y - bodyLength / 2
    const originYs = [footY + 0.05, footY + avatarRadius, center.y]
    let clearance = Number.POSITIVE_INFINITY
    for (const y of originYs) {
      const hit = physicsWorld.castRay(
        new RAPIER.Ray({ x: center.x, y, z: center.z }, direction),
        bodyLength,
        true,
        RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
        undefined,
        avatarCollider,
      )
      if (hit) {
        clearance = Math.min(clearance, hit.timeOfImpact)
      }
    }
    return clearance
  }

  /**
   * 死亡演出を開始する。移動・ジャンプ系の状態を凍結し（倒れポーズに空中ポーズ
   * 等が混ざらないように）、押下中キーも解放して 3 秒後のリスポーンへ備える。
   * 倒れ込む向き（health）は前方・後方のうち障害物までの距離が遠い側を選び、
   * さらに障害物に身体が届かない最大倒れ角を計算して貫通を防ぐ
   * （両側が狭い場所では 90° まで倒さず途中で止める）。
   */
  const enterDeath = (kind: 'health' | 'void'): void => {
    // 乗車中の死亡（車ごと奈落・体力 0）は強制降車する。アバターは座席位置に
    // 留まり、通常の死亡演出 → spawn 復帰の流れに乗る。死亡演出中は
    // stepPhysics がプロンプト更新へ到達しないため、ここで明示的に消す。
    vehicleSystem.abandonVehicle()
    vehicleSystem.clearPrompt()
    let fall: {
      direction: 'forward' | 'backward'
      maxAngleRad: number
    } | null = null
    if (kind === 'health') {
      // three.js 標準: rotation.y = θ のとき forward は (-sin(θ), 0, -cos(θ))。
      const facing = world.getState().get(AVATAR_ID)?.rotationY ?? 0
      const forward = { x: -Math.sin(facing), y: 0, z: -Math.cos(facing) }
      const backward = { x: -forward.x, y: 0, z: -forward.z }
      const forwardClearance = horizontalClearance(forward)
      const backwardClearance = horizontalClearance(backward)
      const direction =
        forwardClearance >= backwardClearance ?
          ('forward' as const)
        : ('backward' as const)
      const clearance = Math.max(forwardClearance, backwardClearance)
      const bodyLength = (avatarHalfHeight + avatarRadius) * 2
      // 倒れた身体の到達距離 ≒ bodyLength × sin(倒れ角)。クリアランス内に
      // 収まる角度まで倒す（十分空いていれば 90°）。
      const maxAngleRad = Math.asin(Math.min(1, clearance / bodyLength))
      fall = { direction, maxAngleRad }
    }
    death = { kind, remaining: RESPAWN_DELAY_SECONDS, fall }
    held.clear()
    virtualHeld.clear()
    heldDashKeys.clear()
    jumpHeldSources.clear()
    velocityY = 0
    isDashing = false
    isSwimmingNow = false
    isSwimmingMovingNow = false
    isSlidingNow = false
    slideSpeed = 0
    platformCarry.clear()
  }

  /** アバターを spawn へ復帰させる（死亡リスポーン用）。操作キーは維持する。 */
  /**
   * アバターを指定位置へ即時テレポートする（リスポーン・ワープギミック共用）。
   * 速度・接地・描画補間をテレポート向けに整える。
   */
  const teleportAvatarTo = (position: {
    x: number
    y: number
    z: number
  }): void => {
    avatarBody.setTranslation(
      { x: position.x, y: position.y, z: position.z },
      true,
    )
    velocityY = 0
    grounded = true
    // 滑走・遊泳の一時状態もクリアする（すべり台から平地へワープした直後に
    // 旧滑走の速度・方向で滑り続けない）。遊泳は次ステップの再判定に任せる。
    isSlidingNow = false
    slideSpeed = 0
    slideGradient = 0
    isSwimmingNow = false
    isSwimmingMovingNow = false
    // 落下中(空中)→ 接地面へのテレポート。レンダラの誤着地屈伸を抑止し、
    // KCC の接地復帰までの数 step は接地を維持する。
    respawnGeneration += 1
    spawnSettleSteps = 2
    world.setPosition(AVATAR_ID, {
      x: position.x,
      y: position.y,
      z: position.z,
    })
    // テレポートは補間しない（移動元からスーッと飛ぶ描画を防ぐ）。
    renderPosSamples = [{ ...position }, { ...position }, { ...position }]
    // テレポート先の足元の台を即時記録する。次の物理ステップまで待つと、
    // その間にプログラムが台を回した場合に回転後の姿勢が基準になり、
    // 差分（追従）を取りこぼす。
    platformCarry.rememberGroundPlatform()
  }

  const respawnAvatar = (): void => {
    teleportAvatarTo(avatar.spawn)
    // 実機の死亡リスポーンは Character 再生成で向きも spawn の向きへ戻る。
    // teleportAvatarTo はワープギミック（向き維持）と共用のため、向きの初期化は
    // 死亡リスポーン専用のここで行う。学習者プログラムがスタート地点を回して
    // いれば、その現在の向きでスポーンする（実機の SpawnLocation と同じ）。
    world.setRotationY(AVATAR_ID, currentSpawnYaw())
    // reset 直後の監視窓が死亡演出（早期 return で未消費）を跨いで生き残らない
    // ように消費する（リスポーン自体がカメラの再スナップを起こすため不要）。
    spawnFacingSnapSteps = 0
  }

  /**
   * アバター中心位置 p で簡易遊泳になるか。水域（terrain.water）の矩形内で、
   * 足元（カプセル下端）が SWIM_MIN_DEPTH 以上水没していれば遊泳。
   * 浅瀬では false（海底を歩く）。
   */
  const isSwimmingAt = (p: { x: number; y: number; z: number }): boolean => {
    if (!waterVolume) {
      return false
    }
    if (
      p.x < waterVolume.x0 ||
      p.x > waterVolume.x1 ||
      p.z < waterVolume.z0 ||
      p.z > waterVolume.z1
    ) {
      return false
    }
    const feetY = p.y - avatar.size.y / 2
    return waterVolume.level - feetY >= SWIM_MIN_DEPTH
  }

  /** 1 物理ステップ分の入力処理・移動・衝突解決・接触検出を行う。 */
  const stepPhysics = (dt: number): void => {
    objectBodyRegistry.sync()
    vehicleSystem.abandonIfSeatMissing()
    // プログラム移動の押し出し要求は同一ステップで消費する（死亡・乗車などの
    // 早期 return 経路で持ち越すと、復帰の瞬間に蓄積分が一気に適用されるため）。
    // 移動ぶんは要求のまま持ち、台追従の後（アバターの最終位置）で解く。
    let programPushRequestsThisStep = programPushRequests
    programPushRequests = null
    let programPushVectorThisStep = programPushVector
    programPushVector = null
    // サイズ変更ぶんは sync 時点で向き・量が確定しているので、そのときに
    // 登録された「押している部品」をそのまま引き継ぐ。
    pushedThisStep = pushingObjectIds
    pushingObjectIds = new Set<string>()
    let programPushExcludes =
      pushingColliderHandles.size > 0 ? new Set(pushingColliderHandles) : null
    pushingColliderHandles.clear()

    // 死亡演出中: アバターは動かさず（倒れる/消えた場所に留まる）、オブジェクト
    // 物理（落下物・転がり等）だけを進める。タイマが尽きたら spawn へ復帰する。
    if (death) {
      // 死亡演出中に積まれたテレポート要求は破棄する（復帰後の生存キャラへの
      // 「予約」にしない。実機でも死亡中の HumanoidRootPart 移動は意味を持たない）。
      world.consumeAvatarTeleport()
      physicsWorld.step()
      objectBodyRegistry.writeBackDynamicBodies()
      death.remaining -= dt
      if (death.remaining <= 0) {
        death = null
        respawnAvatar()
        // 実機のリスポーンは Humanoid 再生成。体力だけでなく、プログラムで
        // 上書きされた歩行速度・ジャンプ力も既定へ戻す。
        world.resetAvatarStats()
      }
      return
    }

    // ワープギミック等からのテレポート要求を適用する（Lua:
    // HumanoidRootPart.Position = ... 相当。死亡演出中は death 分岐が
    // 先に return するため、復帰後の要求のみ処理される）。
    const teleportRequest = world.consumeAvatarTeleport()
    if (teleportRequest) {
      // 乗車中のテレポートは強制降車（実機もシートウェルドが外れる）。
      vehicleSystem.abandonVehicle()
      teleportAvatarTo(teleportRequest)
      // ワープ前の位置で食い込んだ部品の押し出しは破棄する（ワープ先で
      // 無関係な横押し・接触扱いにしない）。
      programPushRequestsThisStep = null
      programPushVectorThisStep = null
      programPushExcludes = null
      pushedThisStep = new Set<string>()
    }

    // 乗車中は歩行系（KCC・台追従・遊泳・滑走・Touched）を使わず、
    // 運転モードが車両とアバターを一体で動かす。乗車中かどうかは
    // activePartIdSet の非 null（= 乗車中）で判定し、分岐内で部品集合が
    // 取れない状態をなくす。
    const seatedPartIds = vehicleSystem.activePartIdSet()
    if (seatedPartIds) {
      vehicleSystem.stepVehicleDrive(dt)
      physicsWorld.step()
      objectBodyRegistry.writeBackDynamicBodies()
      const seatedPosition = avatarBody.translation()
      // 車ごと奈落へ落ちたら通常と同じ void 死亡（enterDeath が降車させる）。
      if (seatedPosition.y < fallThreshold) {
        enterDeath('void')
        return
      }
      if (world.getAvatarHealth() <= 0) {
        enterDeath('health')
        return
      }
      // 実機では車内のアバターの身体もトリガーゾーン（CanCollide=false の
      // Touched）とは重なりで発火する。solid 衝突・足元レイは歩行専用のため、
      // 乗車中は sensor 重なりのみ検知する（車両自身の部品は除外）。
      touchDetection.detectSeatedTouches(seatedPartIds)
      vehicleSystem.updateVehiclePrompt()
      return
    }

    // 乗っている台の回転・移動への追従（入力移動より先に適用する）。
    const carry = platformCarry.computeCarryDelta()
    platformCarry.applyCarry(carry)

    // follow（アバター視点）時はカメラ方位で入力を回転し、視点方向へ進める。
    // それ以外（free/first/未提供）はワールド基準（従来どおり）。
    const rawDirection = directionFromKeys(held, virtualHeld)
    const cameraYaw = getCameraYaw?.() ?? null
    const direction = normalizeXZ(
      cameraYaw === null ? rawDirection : (
        rotateXZByYaw(rawDirection, cameraYaw)
      ),
    )
    const horizontalScale = 1
    // ダッシュ機能は無効化中（歩行モーションを走りに統一したため速度倍率は
    // 使わない）。再有効化する場合は以下のコメントを戻す。
    // const isMovingInput = direction.x !== 0 || direction.z !== 0
    // isDashing = heldDashKeys.size > 0 && isMovingInput
    // const dashScale = isDashing ? DASH_SPEED_MULTIPLIER : 1
    isDashing = false
    const dashScale = 1
    // 簡易遊泳の判定: 水域内で足元が SWIM_MIN_DEPTH 以上水没していれば遊泳。
    // 浅瀬（岸辺）は海底を歩く（Roblox の Humanoid Swimming の簡略化）。
    const currentPosition = avatarBody.translation()
    const swimming = isSwimmingAt(currentPosition)
    // 速度は events_setWalkSpeedTo で書き換え可能。物理ステップ毎に最新値を参照。
    const walkSpeed =
      world.getAvatarWalkSpeed() *
      WALK_SPEED_SCALE *
      (swimming ? SWIM_HORIZONTAL_SCALE : 1)
    let horizontalX = direction.x * walkSpeed * dashScale * dt * horizontalScale
    let horizontalZ = direction.z * walkSpeed * dashScale * dt * horizontalScale
    if (isSlidingNow) {
      // 滑走中は操作不能（実機のすべり台同様）。滑走方向へ滑り降りる。
      horizontalX = slideDirection.x * slideSpeed * dt
      horizontalZ = slideDirection.z * slideSpeed * dt
    }

    let vertical: number
    if (swimming && waterVolume) {
      // 遊泳: 重力の代わりに浮力と抗力。入水時の落下の勢いは指数減衰で殺し、
      // 目標深度（頭が水面から出る）までゆっくり浮上して水面で静止する。
      const surfaceCenterY = waterVolume.level - SWIM_SURFACE_CENTER_DEPTH
      velocityY *= Math.exp(-SWIM_VERTICAL_DRAG * dt)
      if (jumpHeldSources.size > 0) {
        // ジャンプキー押下中は浮上。水面を超えても止めない（岸へ上がる・
        // 水面から跳ね出す操作を許す。出た直後は重力に戻り自然に着水する）。
        // max: 水面でのジャンプ（applyKeyPress の跳躍初速）を上書きで殺さず、
        // 水面から跳び出して岸や低い足場へ上がれるようにする。
        velocityY = Math.max(velocityY, SWIM_UP_SPEED)
      } else if (currentPosition.y < surfaceCenterY - 0.25) {
        velocityY = Math.max(velocityY, SWIM_BUOYANCY_SPEED)
      }
      vertical = velocityY * dt
      // 浮力での浮上は水面（目標深度）で止める（水上へ飛び出してバウンドしない）。
      if (
        jumpHeldSources.size === 0 &&
        velocityY > 0 &&
        currentPosition.y + vertical > surfaceCenterY
      ) {
        vertical = Math.max(0, surfaceCenterY - currentPosition.y)
        velocityY = 0
      }
    } else {
      velocityY -= GRAVITY * dt
      vertical = velocityY * dt
      // 接地中は重力でこれ以上床へ押し込まない。KCC は薄い床（厚み1程度＝スポーン台）に
      // 対し毎フレームの微小な下方移動を取りこぼして徐々に沈み、下の床（ベースプレート等）
      // まで貫通することがある（足が台へ埋まる原因）。接地維持は snapToGround に任せ、
      // 下方向の押し込みを 0 にして沈降を防ぐ。落下・ジャンプ（非接地）は従来どおり。
      if (grounded && vertical < 0) {
        vertical = 0
      }
      // 滑走中は斜面に貼り付くよう、水平移動ぶんの下降を desired に含める
      // （snapToGround の範囲(0.5)を超える高速滑走でも斜面から浮かない）。
      if (isSlidingNow && grounded && slideGradient > 0) {
        vertical = -slideSpeed * dt * slideGradient
      }
    }

    // プログラム移動/サイズ拡大する solid 部品に巻き込まれた押し出し。
    // 台追従（applyCarry）適用後の現在位置で重なりと必要量を解き、歩行と同じ
    // KCC 移動で解決する（進路上の壁では止まる）。
    const avatarNow = avatarBody.translation()
    const resizePush = programPushVectorThisStep ?? { x: 0, z: 0 }
    const movePushedIds = new Set<string>()
    const movePushExcludes = new Set<number>()
    const moveRequests = programPushRequestsThisStep
    const movePlanes =
      moveRequests ?
        resolveProgramPushRequests(
          moveRequests,
          avatarNow,
          movePushedIds,
          movePushExcludes,
        )
      : null
    const programPush =
      movePlanes ? resolveProgramPush(movePlanes, avatarNow) : { x: 0, z: 0 }
    if (movePushedIds.size > 0) {
      // 実際に押した部品だけを、このステップの「押している部品」へ足す
      // （KCC の障害物から外し、Touched を接触として補完する対象）。
      pushedThisStep = new Set([...pushedThisStep, ...movePushedIds])
      programPushExcludes = new Set([
        ...(programPushExcludes ?? []),
        ...movePushExcludes,
      ])
    }
    const totalPush = {
      x: programPush.x + resizePush.x,
      z: programPush.z + resizePush.z,
    }
    if (totalPush.x === 0 && totalPush.z === 0) {
      // carry 等で既に分離平面を満たしていれば、このステップは「押していない」。
      // KCC 除外・Touched 補完も落とし、押していない部品の接触扱いを残さない。
      programPushExcludes = null
      pushedThisStep = new Set<string>()
    }
    const desired = {
      x: mergePushAxis(horizontalX, totalPush.x),
      y: vertical,
      z: mergePushAxis(horizontalZ, totalPush.z),
    }
    // rapier の KCC は既定で sensor も障害物として扱うため、明示的に除外する。
    // 除外しないと CanCollide=false（sensor モード）の部品がアバターの移動を阻み、
    // unsetCanCollide で「すり抜けられる」はずの壁を通り抜けられない。
    // sensor との接触検知は detectTouches の intersectionPairsWith が担う。
    // 押している部品自身も障害物から除外する（食い込んだ状態からの押し出しが
    // その部品自身にブロックされないようにする）。
    const solveMovement = (want: {
      x: number
      y: number
      z: number
    }): { x: number; y: number; z: number } => {
      const excludes = programPushExcludes
      characterController.computeColliderMovement(
        avatarCollider,
        want,
        RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
        undefined,
        excludes ? (collider) => !excludes.has(collider.handle) : undefined,
      )
      const movement = characterController.computedMovement()
      return { x: movement.x, y: movement.y, z: movement.z }
    }
    let corrected = solveMovement(desired)
    // 回した部品の面は斜めなので、必要な退避量はアバターの X/Y/Z すべてで
    // 変わる。押し出し量は「移動前の位置」で解いているが、実際の着地点は
    // 歩行入力・重力・KCC の補正（壁でのクリップ）を通ったあとの位置になる。
    // そこで着地点で解き直し、まだ食い込んでいれば不足ぶんを足して 1 度だけ
    // 移動を計算し直す（押している部品は KCC から除外されるため、この
    // ステップ内で解消しないと食い込んだまま残る）。
    // 既知の限界: 壁と部品に挟まれて KCC が押し出しを止める場合は食い込みが
    // 残る（実機の圧死・吹き飛びまでは再現しない。resolveProgramPush 参照）。
    if (moveRequests) {
      const landed = {
        x: avatarNow.x + corrected.x,
        y: avatarNow.y + corrected.y,
        z: avatarNow.z + corrected.z,
      }
      const landedIds = new Set<string>()
      const landedExcludes = new Set<number>()
      const landedPlanes = resolveProgramPushRequests(
        moveRequests,
        landed,
        landedIds,
        landedExcludes,
      )
      const shortfall =
        landedPlanes ? resolveProgramPush(landedPlanes, landed) : { x: 0, z: 0 }
      if (shortfall.x !== 0 || shortfall.z !== 0) {
        // 移動前は触れていなくても、歩行や落下で自分から部品へ入り込むことが
        // ある。着地点で初めて食い込む場合もここで押し出しに数える。
        pushedThisStep = new Set([...pushedThisStep, ...landedIds])
        programPushExcludes = new Set([
          ...(programPushExcludes ?? []),
          ...landedExcludes,
        ])
        corrected = solveMovement({
          x: corrected.x + shortfall.x,
          y: desired.y,
          z: corrected.z + shortfall.z,
        })
      }
    }

    const current = avatarBody.translation()
    const next = {
      x: current.x + corrected.x,
      y: current.y + corrected.y,
      z: current.z + corrected.z,
    }
    avatarBody.setNextKinematicTranslation(next)

    physicsWorld.step()
    objectBodyRegistry.writeBackDynamicBodies()

    const resolved = avatarBody.translation()
    world.setPosition(AVATAR_ID, {
      x: resolved.x,
      y: resolved.y,
      z: resolved.z,
    })
    // 描画補間用に直近 3 ステップの確定位置を保持する（rAF 側で補間）。
    renderPosSamples = [
      renderPosSamples[1],
      renderPosSamples[2],
      { x: resolved.x, y: resolved.y, z: resolved.z },
    ]

    // 奈落（シーン最下面より下）へ落ちたら死亡演出（void）へ。アバターは消え、
    // カメラは消えた場所に留まり、3 秒後に spawn へ復帰する（GAP-FALLOUT）。
    if (resolved.y < fallThreshold) {
      enterDeath('void')
      return
    }

    // 体力 0 以下で死亡演出（health）へ。倒れるポーズで操作不能になり、
    // 3 秒後に spawn へ復帰して体力を回復する（events_setHealthTo(0) 等）。
    // Roblox の Humanoid 死亡 → リスポーンの再現。
    if (world.getAvatarHealth() <= 0) {
      enterDeath('health')
      return
    }

    // 接地・姿勢の確定は「移動後」の位置で遊泳を再判定する。移動前の判定
    // （swimming）のまま grounded=true で抜けると、このステップで水面の外へ
    // 出た直後に Space を押したとき、stale な接地扱いで陸上ジャンプ
    // （JumpPower=50）へ昇格してしまう。
    isSwimmingNow = isSwimmingAt(resolved)
    isSwimmingMovingNow =
      isSwimmingNow && (direction.x !== 0 || direction.z !== 0)
    if (isSwimmingNow) {
      // 遊泳中に接地概念はない。落下ポーズ・着地屈伸を出さないよう接地扱いに
      // する（レンダラは grounded で姿勢を決める）。
      grounded = true
      spawnSettleSteps = 0
      // 滑走のまま入水した場合は滑走を解除する（この分岐は updateSlideState の
      // 前に return するため、ここで明示的に落とさないと滑走状態が残る）。
      isSlidingNow = false
      slideSpeed = 0
      slideGradient = 0
      if (direction.x !== 0 || direction.z !== 0) {
        world.setRotationY(AVATAR_ID, Math.atan2(-direction.x, -direction.z))
      }
      platformCarry.rememberGroundPlatform()
      vehicleSystem.updateVehiclePrompt()
      touchDetection.detectTouches()
      return
    }

    const wasGrounded = grounded
    // computedGrounded に加えて足元レイでも接地を判定する。autostep で段差の縁に
    // 乗り上げた瞬間、KCC は computedGrounded=false を返し続けることがあり
    // （カプセルが段差角に楔状に接触する姿勢）、その間 velocityY が毎フレーム
    // 加算されて強い下向き desired が段差登りを打ち消し、縁で永久に進めなくなる。
    // レイが床を示すなら接地扱いにして velocityY を切り、登りを完遂させる。
    // - 上昇中（velocityY > 0、ジャンプ直後）はレイ判定を使わず跳躍を妨げない。
    // - margin は通常は足裏ぴったり（GROUND_CONTACT_MARGIN）。広い margin を常用
    //   すると床上 0.3 studs で浮いたまま接地扱いになり落下が止まるため、autostep が
    //   持ち上げている最中（corrected.y > 0）だけ広い margin で楔状態を救済する。
    // 下り坂・段差を下りながら歩く間は、足裏ぴったりの margin（0.06）だと坂面の
    // 微小な落差でレイが届かず grounded がチラつき、走行モーションが両脚揃えの空中
    // ポーズへ飛んで「坂できちんと走れない」見え方になる。直前接地かつ移動入力中は
    // snapToGround(0.5) が位置を坂面へ吸着している範囲を信頼し、autostep と同じ広い
    // margin（0.3）で接地を拾う。ジャンプ降下は直前非接地（wasGrounded=false）、
    // 離陸は下の velocityY>0 で除外されるため、空中での早期着地は招かない。
    const walkingOnGround =
      wasGrounded && (direction.x !== 0 || direction.z !== 0)
    const groundRayMargin =
      corrected.y > 0 || walkingOnGround ?
        GROUND_TOUCH_MARGIN
      : GROUND_CONTACT_MARGIN
    const groundedNow =
      characterController.computedGrounded() ||
      (velocityY <= 0 && castGroundRay(groundRayMargin) !== null)
    if (groundedNow) {
      velocityY = 0
      grounded = true
      spawnSettleSteps = 0
    } else if (spawnSettleSteps > 0 && wasGrounded) {
      // spawn/テレポート直後で KCC がまだ接地を返さない猶予中。実際は spawn 台に
      // 乗っているので接地を維持し、偽の落下・着地屈伸を防ぐ。
      velocityY = 0
      grounded = true
      spawnSettleSteps -= 1
    } else {
      grounded = false
      spawnSettleSteps = 0
    }

    // すべり台滑走の状態遷移（接地確定後の位置・接地相手で判定）。
    updateSlideState(dt)

    if (isSlidingNow) {
      // 滑走中は下り方向（足側が進行方向）を向く。倒れポーズと合わせて
      // 「仰向けで足から滑り降りる」見え方になる。
      world.setRotationY(
        AVATAR_ID,
        Math.atan2(-slideDirection.x, -slideDirection.z),
      )
    } else if (direction.x !== 0 || direction.z !== 0) {
      // three.js 標準: object の rotation.y = θ のとき world forward は
      //   (-sin(θ), 0, -cos(θ))
      // 移動方向 (dx, dz) を forward に一致させたい → -sin(θ) = dx, -cos(θ) = dz
      // すなわち θ = atan2(-dx, -dz)。これにより
      //   W (-Z) → θ=0 (default forward), S (+Z) → θ=π, A (-X) → θ=π/2, D (+X) → θ=-π/2
      // となり、レンダラの follow / first カメラと一貫した向きになる。
      const angle = Math.atan2(-direction.x, -direction.z)
      world.setRotationY(AVATAR_ID, angle)
    } else if (carry.deltaYaw !== 0) {
      // 台の回転に向きも追従させる（実機で台ごと回る挙動の再現）。
      // 滑走・移動入力で向きが決まるステップではそちらを優先する
      // （Humanoid は移動方向を向くため、歩行中は入力の向きが正）。
      const avatarState = world.getState().get(AVATAR_ID)
      if (avatarState) {
        world.setRotationY(AVATAR_ID, avatarState.rotationY + carry.deltaYaw)
        // reset 直後の監視窓: 再生開始と同時の学習者プログラム
        // （スタートしたとき → ◯どまわす）がスタート地点を回した場合、
        // 台の追従でアバターは回るがカメラのスナップは reset 時の向きで
        // 消費済みのため、respawnGeneration を進めてレンダラに背後への
        // 再スナップを要求する。移動入力や滑走で向きが変わるケースと
        // 区別するため、carry のヨー適用時だけ発火させる。
        if (
          spawnFacingSnapSteps > 0 &&
          Math.abs(
            normalizeAngleDelta(
              avatarState.rotationY + carry.deltaYaw - spawnFacingYawAtReset,
            ),
          ) > 1e-6
        ) {
          spawnFacingSnapSteps = 0
          respawnGeneration += 1
        }
      }
    }

    // 監視窓の消費（発火の有無に関わらず数ステップで失効させ、プレイ中の
    // 台回転で毎回カメラが跳ねないようにする）。
    if (spawnFacingSnapSteps > 0) {
      spawnFacingSnapSteps -= 1
    }

    // 次ステップの追従基準（乗っている台の姿勢）を移動確定後の位置で記録する。
    platformCarry.rememberGroundPlatform()

    // 乗降プロンプト（「のる」）の対象を移動確定後の位置で更新する。
    vehicleSystem.updateVehiclePrompt()

    touchDetection.detectTouches()
  }

  /**
   * アバター中心から足元へ短いレイを撃ち、接地している相手コライダーを返す。
   * レイ長はカプセル足裏まで + margin。sensor は「接地」にならないため除外し、
   * 自分自身もヒット対象から外す。接地判定の補完（autostep 中の楔状態の脱出）と
   * 足元 Touched 検知の両方で使う（margin は呼び出し側が用途に応じて選ぶ）。
   */
  const castGroundRay = (margin: number): RAPIER.Collider | null => {
    const rayOrigin = avatarBody.translation()
    const groundHit = physicsWorld.castRay(
      new RAPIER.Ray(rayOrigin, GROUND_RAY_DIRECTION),
      avatarHalfHeight + avatarRadius + margin,
      true,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      avatarCollider,
    )
    return groundHit ? groundHit.collider : null
  }

  /**
   * 足元レイで接地相手と接地面の法線を返す（castGroundRay の法線付き版）。
   * すべり台の滑走判定と滑走方向（最大勾配の下り）の算出に使う。
   */
  const castGroundRayWithNormal = (
    margin: number,
  ): { collider: RAPIER.Collider; normal: RAPIER.Vector } | null => {
    const rayOrigin = avatarBody.translation()
    const hit = physicsWorld.castRayAndGetNormal(
      new RAPIER.Ray(rayOrigin, GROUND_RAY_DIRECTION),
      avatarHalfHeight + avatarRadius + margin,
      true,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      avatarCollider,
    )
    return hit ? { collider: hit.collider, normal: hit.normal } : null
  }

  /**
   * すべり台滑走の状態遷移（毎ステップ、接地確定後に呼ぶ）。
   * - 接地相手が「すべり台」: 滑走に入り、最大勾配の下り方向
   *   （法線の水平成分の向き）へ加速する。
   * - 台の外: 接地中は摩擦で減速し、十分遅くなったら通常操作へ戻す。
   *   空中（台の終端から飛び出した直後）は速度を保ち、慣性で飛ぶ。
   */
  const updateSlideState = (dt: number): void => {
    const hit = castGroundRayWithNormal(GROUND_TOUCH_MARGIN)
    const groundId =
      hit ? objectBodyRegistry.objectIdOf(hit.collider.handle) : undefined
    const groundName =
      groundId !== undefined ? world.getState().get(groundId)?.name : undefined
    // 滑走の開始・加速は「接地中」のみ（斜面上空をかすめただけでは入らない）。
    if (grounded && hit && groundName === SLIDE_OBJECT_NAME) {
      const horizontalMag = Math.hypot(hit.normal.x, hit.normal.z)
      // 斜面（法線が傾いている）でのみ滑走する。台の水平な部分では入らない。
      if (horizontalMag > 1e-3 && hit.normal.y > 1e-3) {
        slideDirection = {
          x: hit.normal.x / horizontalMag,
          z: hit.normal.z / horizontalMag,
        }
        slideGradient = horizontalMag / hit.normal.y
        if (!isSlidingNow) {
          isSlidingNow = true
          slideSpeed = Math.max(slideSpeed, world.getAvatarWalkSpeed() * 0.5)
        }
        slideSpeed = Math.min(
          SLIDE_MAX_SPEED,
          slideSpeed + SLIDE_ACCELERATION * dt,
        )
        return
      }
    }
    if (!isSlidingNow) {
      return
    }
    // 台の外。接地中のみ摩擦で減速する（空中は慣性を保つ）。
    if (grounded) {
      slideGradient = 0
      slideSpeed = Math.max(0, slideSpeed - SLIDE_FRICTION * dt)
      if (slideSpeed <= SLIDE_EXIT_SPEED) {
        isSlidingNow = false
        slideSpeed = 0
      }
    }
  }

  // 乗っている台（回転・移動するプラットフォーム）への追従は platformCarry
  // サブシステムへ委譲する。台の姿勢スナップショット（carriedPlatform）は
  // 内部に隠蔽し、本体は clear()（死亡・乗車・reset 時）と各操作だけを呼ぶ。
  const platformCarry = createPlatformCarry({
    physicsWorld,
    world,
    avatarBody,
    registry: objectBodyRegistry,
    getAvatarCollider: () => avatarCollider,
    castGroundRay,
    isGrounded: () => grounded,
    isSwimming: () => isSwimmingNow,
    isDead: () => death !== null,
  })

  /**
   * 乗車時に歩行系の一時状態を一括解除する（座席へ瞬間移動するため誤検知を
   * 防ぐ）。テレポート扱い（respawnGeneration 加算）にして、レンダラの
   * 誤着地屈伸・距離スパイクを抑止する。歩行物理の可変状態は本体が所有し、
   * vehicleSystem へはこの意図単位のメソッドだけを注入する。
   */
  const resetWalkStateForVehicleEntry = (): void => {
    velocityY = 0
    grounded = true
    isSlidingNow = false
    slideSpeed = 0
    slideGradient = 0
    isSwimmingNow = false
    isSwimmingMovingNow = false
    platformCarry.clear()
    respawnGeneration += 1
  }

  /**
   * 描画補間サンプルを指定位置へスナップする（補間しない）。乗車中の
   * 座席同期で、補間遅延により座席から遅れて見えるのを防ぐ。
   */
  const snapRenderPositionTo = (position: Vec3): void => {
    renderPosSamples = [{ ...position }, { ...position }, { ...position }]
  }

  // 車両の乗車・運転・降車は vehicleSystem サブシステムへ委譲する。
  // 乗車状態（activeVehicle）・プロンプト対象（nearbyVehicle）・UI 通知の
  // 既知値は内部に隠蔽し、本体は状態参照（isSeated / activePartIdSet）と
  // 操作（triggerVehicleAction / abandonVehicle / clearPrompt /
  // updateVehiclePrompt / stepVehicleDrive）だけを呼ぶ。
  const vehicleSystem = createVehicleSystem({
    physicsWorld,
    world,
    avatarBody,
    registry: objectBodyRegistry,
    getAvatarCollider: () => avatarCollider,
    avatar,
    vehicleDescriptors: descriptor.vehicles ?? [],
    isDisposed: () => disposed,
    isPaused: () => paused,
    isDead: () => death !== null,
    isKeyHeld: (code) => held.has(code) || virtualHeld.has(code),
    isJumpHeld: () => jumpHeldSources.size > 0,
    resetWalkStateForVehicleEntry,
    snapRenderPositionTo,
    teleportAvatarTo,
    onVehiclePrompt,
    // 乗車中に車体が触れた CanTouch 部品も歩行時と同じ Touched 経路へ流す。
    onVehicleTouch: onTouch,
  })

  // Touched イベントの 3 系統検知（solid 衝突・足元レイ・sensor 重なり）は
  // touchDetection サブシステムへ委譲する。接触履歴（prevTouched）は内部に
  // 隠蔽し、本体は reset 時に clear() を呼ぶ。
  const touchDetection = createTouchDetection({
    physicsWorld,
    characterController,
    world,
    registry: objectBodyRegistry,
    getAvatarCollider: () => avatarCollider,
    castGroundRay,
    getPushedObjectIds: () => pushedThisStep,
    onTouch,
  })

  const onAnimationFrame = (now: number): void => {
    if (disposed) {
      return
    }
    const elapsed = Math.min((now - lastTime) / 1000, MAX_FRAME_DT)
    lastTime = now
    // 一時停止中は時間だけ進めて物理ステップを完全に止める（シーンを凍結）。
    if (!paused) {
      accumulator += elapsed
      while (accumulator >= FIXED_STEP) {
        stepPhysics(FIXED_STEP)
        accumulator -= FIXED_STEP
      }
      // 最後の完了ステップの仮想時刻。残り accumulator ぶんだけ now より前。
      lastStepSettledAtMs = now - accumulator * 1000
    }
  }

  // キーボードと仮想キー（キーガイドのタップ等）で共通のキー押下処理。
  // 呼び出し側が一時停止・死亡演出・テキスト入力中のガードを済ませている前提。
  // targetHeld はソース別の押下集合（キーボード = held、仮想キー = virtualHeld）。
  // ダッシュ修飾キーは現状キーボードのみのため共有集合のままにしている。
  const applyKeyPress = (code: string, targetHeld: Set<string>): void => {
    // Space でジャンプ。接地時のみ受理してその場で離陸する（Roblox 同様、
    // 予備動作（屈伸）は挟まない）。
    if (code === 'KeyE') {
      // 乗降キー（実機の ProximityPrompt / ExitSeat の E と同じ）。
      vehicleSystem.triggerVehicleAction()
      return
    }
    if (code === 'Space') {
      // 乗車中の Space はハンドブレーキ（実機の運転席はジャンプ降車できない）。
      // 押下記録だけ行い、stepVehicleDrive が jumpHeldSources を参照する。
      if (vehicleSystem.isSeated()) {
        jumpHeldSources.add(targetHeld === held ? 'physical' : 'virtual')
        return
      }
      // 滑り台の滑走中は操作不能（実機同様）。ジャンプで脱出できない。
      if (isSlidingNow) {
        return
      }
      // 押しっぱなし状態を記録（遊泳中の浮上は stepPhysics がこれを参照する）。
      jumpHeldSources.add(targetHeld === held ? 'physical' : 'virtual')
      // 遊泳中は陸上ジャンプ（JumpPower=既定 50）ではなく控えめな水中ジャンプ。
      // 遊泳中の grounded は表示用に true を維持しているため、ここで明示的に
      // 分岐しないと水面から陸上ジャンプの高さで飛び出してしまう。
      if (isSwimmingAt(avatarBody.translation())) {
        velocityY = Math.max(velocityY, SWIM_JUMP_SPEED)
        return
      }
      if (grounded) {
        // ジャンプ初速は events_setJumpPowerTo で変わり得る（既定 50）。
        velocityY = world.getAvatarJumpPower() * JUMP_POWER_SCALE
        grounded = false
      }
      return
    }
    // Shift はダッシュ修飾キー。移動キーと併用で速度を上げる（押下のみ記録）。
    if (DASH_KEYS.has(code)) {
      heldDashKeys.add(code)
      return
    }
    if (MOVEMENT_KEYS.has(code)) {
      targetHeld.add(code)
    }
  }

  const applyKeyRelease = (code: string, targetHeld: Set<string>): void => {
    if (code === 'Space') {
      jumpHeldSources.delete(targetHeld === held ? 'physical' : 'virtual')
      return
    }
    if (DASH_KEYS.has(code)) {
      heldDashKeys.delete(code)
      return
    }
    if (MOVEMENT_KEYS.has(code)) {
      targetHeld.delete(code)
    }
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    if (isTextEntryTarget(event.target)) {
      return
    }
    // 一時停止中（停止ボタン押下後）と死亡演出中はキャラクター操作を受け付けない。
    // ただし操作キーのブラウザ既定動作（Space/矢印のスクロール等）は
    // 通常時と同様に抑止し、その間だけページが動く副作用を防ぐ。
    if (paused || death !== null) {
      if (event.code === 'Space' || ARROW_KEYS.has(event.code)) {
        event.preventDefault()
      }
      return
    }
    if (event.code === 'Space') {
      // OS のキーリピートは無視する（押しっぱなしで連続ジャンプさせない）。
      if (event.repeat) return
      event.preventDefault()
      applyKeyPress(event.code, held)
      return
    }
    // 乗降キーのキーリピートは無視する（押しっぱなしで乗降が連打されない）。
    if (event.code === 'KeyE' && event.repeat) {
      return
    }
    // 自分が処理するキーのみ preventDefault（矢印キーのページスクロール防止）。
    if (ARROW_KEYS.has(event.code)) {
      event.preventDefault()
    }
    applyKeyPress(event.code, held)
  }

  const onKeyUp = (event: KeyboardEvent): void => {
    applyKeyRelease(event.code, held)
  }

  // キー押下中にフォーカスが外れる（Blockly 等のクリック、タブ・ウィンドウ
  // 切替）と keyup を取り逃して動きっぱなしになるため、フォーカス喪失時は
  // 物理キーの押下状態を全解放する（仮想キーはポインタ操作なので対象外）。
  const releasePhysicalKeys = (): void => {
    held.clear()
    heldDashKeys.clear()
    jumpHeldSources.delete('physical')
  }

  // フォーカス可能にし、クリックで自身へフォーカスを移してキー入力を受けられるようにする。
  const onPointerDown = (): void => {
    keyboardTarget.focus()
  }
  if (keyboardTarget.tabIndex < 0) {
    keyboardTarget.tabIndex = 0
  }
  keyboardTarget.addEventListener('keydown', onKeyDown)
  keyboardTarget.addEventListener('keyup', onKeyUp)
  keyboardTarget.addEventListener('pointerdown', onPointerDown)
  keyboardTarget.addEventListener('blur', releasePhysicalKeys)

  lastTime = performance.now()
  // rAF ループと window blur 購読（keyboardTarget 自身の blur だけでは、要素は
  // フォーカスされたままウインドウ全体が非アクティブになるケース〔タブ・アプリ
  // 切替〕を捉えられないため window の blur も監視する必要がある）を、PIP で
  // keyboardTarget が別ウインドウへ移動しても追従する形でまとめて管理する。
  const lifecycle = startKeyboardTargetLifecycle(
    keyboardTarget,
    onAnimationFrame,
    [releasePhysicalKeys],
  )

  const reset = (): void => {
    if (disposed) {
      return
    }
    // 乗車状態を解除する（停止ボタン・再実行で乗車が残留しない）。部品の姿勢は
    // 呼び出し側の world.reset() と objectBodyRegistry.reset() が初期位置へ戻す。
    vehicleSystem.abandonVehicle()
    vehicleSystem.clearPrompt()
    // アバターを spawn へ即時テレポート。kinematic なので setTranslation + step で確定。
    avatarBody.setTranslation(
      { x: avatar.spawn.x, y: avatar.spawn.y, z: avatar.spawn.z },
      true,
    )
    velocityY = 0
    grounded = true
    // リセットは即時復帰（死亡演出はおためしプレイの再生中のみの体験）。
    death = null
    heldDashKeys.clear()
    isDashing = false
    isSwimmingNow = false
    isSwimmingMovingNow = false
    isSlidingNow = false
    slideSpeed = 0
    // spawn へのテレポート。レンダラの誤着地屈伸を抑止し、KCC の接地復帰までの
    // 数 step は接地を維持する（respawnAvatar と同様）。
    respawnGeneration += 1
    spawnSettleSteps = 2
    // 再生開始直後のプログラムがスタート地点を回すケースに備え、直後の
    // 数ステップは「向きが変わったらカメラを再スナップ」の監視をする。
    spawnFacingSnapSteps = SPAWN_FACING_SNAP_WINDOW_STEPS
    // スタート地点の姿勢を初期へ戻す。停止ボタンのように呼び出し側が
    // world.reset() を伴わない経路でも、スタート地点とアバターの向きが
    // 最初の状態へ戻るようにする（下の objectBodyRegistry.reset() は物理
    // body を descriptor 初期姿勢へ戻すため、World 側もここで揃える）。
    if (spawnObjectId) {
      const spawnObject = descriptor.objects.find((o) => o.id === spawnObjectId)
      if (spawnObject) {
        world.setOrientation(
          spawnObjectId,
          spawnObject.orientation ?? { x: 0, y: 0, z: 0, w: 1 },
        )
        world.setRotationY(
          spawnObjectId,
          spawnObject.orientation ? 0 : (spawnObject.rotationY ?? 0),
        )
      }
    }
    // アバターも初期のスポーン向きへ戻す（スタート地点は直前で初期姿勢に
    // 戻したため currentSpawnYaw() ではなく spawnYaw そのもの）。
    world.setRotationY(AVATAR_ID, avatar.spawnYaw ?? 0)
    spawnFacingYawAtReset = avatar.spawnYaw ?? 0
    touchDetection.clear()
    held.clear()
    virtualHeld.clear()
    jumpHeldSources.clear()
    accumulator = 0
    platformCarry.clear()
    // テレポートは補間しない（respawnAvatar と同様、spawn へ即時スナップ）。
    renderPosSamples = [
      { ...avatar.spawn },
      { ...avatar.spawn },
      { ...avatar.spawn },
    ]
    // World 側も即時に spawn へ同期する（respawnAvatar と同様）。呼び出し側が
    // world.reset() を併用しない経路や、次の物理ステップまでの間に World を
    // 読む経路（カメラ・判定）が旧位置を見ないようにする。
    world.setPosition(AVATAR_ID, {
      x: avatar.spawn.x,
      y: avatar.spawn.y,
      z: avatar.spawn.z,
    })
    // オブジェクト body も初期状態へ戻す（動的化して動いた部品の確実な復帰）。
    objectBodyRegistry.reset()
    // spawn の足元の台を即時記録する。プレイ開始直後（次の物理ステップ前）に
    // プログラムが台を回す場合（プレイしたとき → ただちに 45 どまわす 等）、
    // ステップ末の記録を待つと回転後の姿勢が基準になり追従を取りこぼす。
    // 記録前にクエリを再構築する（テレポート直後の古い構造でレイを外さない）。
    // margin は通常の接地記録と同じ TOUCH を使い、spawn と台の微小ギャップを吸収する。
    warmUpQueries()
    platformCarry.rememberGroundPlatform()
  }

  /**
   * アバターの向きと台追従のヨー基準を、スタート地点の現在の向きへ即時整合
   * させる（実行開始時に runtime.start() の直後に呼ぶ）。「スタートしたとき」
   * の最初の文がスタート地点を回した場合、carry 追従（次の物理ステップ）と
   * 監視窓のカメラ再スナップを待つと、回転前の向きが数フレーム描画されて
   * 「一瞬前の角度」に見えるため、描画前に向きを確定させる。
   *
   * 注意: runtime.start() から同期で走るのは各ハンドラの最初の文までで、
   * 2 文目以降（microtask）の回転はここでは拾えない。そちらは従来どおり
   * 監視窓（spawnFacingSnapSteps）の carry 検知でカメラを再スナップさせる。
   */
  const syncSpawnFacing = (): void => {
    if (disposed) {
      return
    }
    const yaw = currentSpawnYaw()
    world.setRotationY(AVATAR_ID, yaw)
    // 台追従のヨー基準だけ回転後の値へ取り直す（次の物理ステップの carry が
    // 同じ回転をもう一度アバターへ適用して二重回転しないようにする）。位置の
    // 基準は保持し、同時に動かされたスタート地点の移動 carry は消さない。
    if (spawnObjectId) {
      platformCarry.rebaseYaw(spawnObjectId)
    }
    // 監視窓は reset() の設定を維持し、基準ヨーだけ整合後の値へ更新する。
    spawnFacingYawAtReset = yaw
  }

  const setPaused = (next: boolean): void => {
    if (disposed || paused === next) {
      return
    }
    paused = next
    if (next) {
      // 押しっぱなしのキーを解放し、再開時に勝手に動き出さないようにする。
      held.clear()
      virtualHeld.clear()
      heldDashKeys.clear()
      jumpHeldSources.clear()
      isDashing = false
      // 一時停止中は物理 step が止まり motion state が凍結されるため、
      // 入力由来のフラグはここで落とす（停止中の俯瞰で平泳ぎが続かない）。
      isSwimmingMovingNow = false
    } else {
      // 再開時は描画補間サンプルを現在位置へスナップする。一時停止中は
      // lastStepSettledAtMs が止まり表示は最新サンプルへ張り付くため、
      // 古い 3 サンプルのまま再開すると表示が一瞬過去位置へ巻き戻る。
      const current = avatarBody.translation()
      renderPosSamples = [
        { x: current.x, y: current.y, z: current.z },
        { x: current.x, y: current.y, z: current.z },
        { x: current.x, y: current.y, z: current.z },
      ]
    }
  }

  const getMotionState = (nowMs?: number): AvatarMotionState => {
    // 死亡中はモーション状態を凍結し、倒れ/消失ポーズに空中・走行ポーズが
    // 混ざらないようにする（空中で被弾した場合など）。位置も凍結（World の
    // 位置のまま）なので補間位置は返さない。
    if (death) {
      return {
        grounded: true,
        verticalVelocity: 0,
        isDashing: false,
        isSwimming: false,
        isSwimmingMoving: false,
        isSliding: false,
        isSeated: false,
        respawnGeneration,
        deathKind: death.kind,
        deathFall: death.fall,
        renderPosition: null,
      }
    }
    return {
      grounded,
      verticalVelocity: velocityY,
      isDashing,
      isSwimming: isSwimmingNow,
      isSwimmingMoving: isSwimmingMovingNow,
      isSliding: isSlidingNow,
      isSeated: vehicleSystem.isSeated(),
      respawnGeneration,
      deathKind: null,
      deathFall: null,
      // 乗車中は補間しない（World の位置 = 車体と同じ物理確定位置で描き、
      // 補間遅延で座席からズレて見えるのを防ぐ）。
      renderPosition:
        vehicleSystem.isSeated() ? null : (
          interpolatedRenderPosition(nowMs ?? performance.now())
        ),
    }
  }

  /**
   * 表示用アバター位置。最新ステップ時刻からの経過 w（ステップ単位、vsync
   * 基準の同一時計で評価）に応じて、直近 3 サンプルを区分線形補間する。
   * 表示時刻 = 最新ステップの 2 ステップ前 + w。レンダラが物理より先行する
   * フレーム（w が 1 を超える）でもサンプル区間内に収まり、跳ねない。
   */
  const interpolatedRenderPosition = (
    nowMs: number,
  ): { x: number; y: number; z: number } => {
    const w = (nowMs - lastStepSettledAtMs) / 1000 / FIXED_STEP
    if (w <= 1) {
      // interpolateStepPositions が下限 0 にクランプする。
      return interpolateStepPositions(
        renderPosSamples[0],
        renderPosSamples[1],
        w,
      )
    }
    // 物理が 2 フレーム以上止まった場合は上限（最新サンプル）にクランプされる。
    return interpolateStepPositions(
      renderPosSamples[1],
      renderPosSamples[2],
      w - 1,
    )
  }

  const dispose = (): void => {
    if (disposed) {
      return
    }
    disposed = true
    lifecycle.dispose()
    keyboardTarget.removeEventListener('keydown', onKeyDown)
    keyboardTarget.removeEventListener('keyup', onKeyUp)
    keyboardTarget.removeEventListener('pointerdown', onPointerDown)
    keyboardTarget.removeEventListener('blur', releasePhysicalKeys)
    objectBodyRegistry.dispose()
    held.clear()
    virtualHeld.clear()
    jumpHeldSources.clear()
    // rapier の WASM 借用エラー（"attempted to take ownership ... while it was borrowed"）
    // が React の ErrorBoundary に伝播するとプレビュー全体が落ちる。
    // dispose は幂等であるべきなので、free() を個別に try/catch する。
    // characterController は World が保持しているため明示 free しない（World 側で一括解放）。
    try {
      physicsWorld.free()
    } catch (error) {
      console.warn('rapier physicsWorld の解放に失敗しました', error)
    }
  }

  // 画面上の操作ボタンからの仮想キー入力。キーボードと同じ意味論で処理する。
  // 一時停止・死亡演出中は押下を無視する（離しは取りこぼすと押しっぱなしに
  // なるため常に処理する）。
  const setVirtualKey = (code: string, pressed: boolean): void => {
    if (disposed) {
      return
    }
    if (pressed) {
      if (paused || death !== null) {
        return
      }
      applyKeyPress(code, virtualHeld)
    } else {
      applyKeyRelease(code, virtualHeld)
    }
  }

  return {
    reset,
    syncSpawnFacing,
    setPaused,
    dispose,
    getMotionState,
    setVirtualKey,
    triggerVehicleAction: vehicleSystem.triggerVehicleAction,
  }
}
